import { ChangeDetectionStrategy, Component, computed, inject, SecurityContext } from '@angular/core'

import { RouterLink } from '@angular/router'
import { AppSignals, CommonService, DealerService } from '@appShared/services'
import { SelectedDealerHeroComponent } from '@appShared/components/selected-dealer-hero/selected-dealer-hero.component'
import { SITE_DIRECTORY_ROUTES, RouteModel } from '@appShared/site-directory.routes'
import { GEN2_ROUTES } from '@appShared/gen2.routes'
import { EXTERNAL_URIS } from '@appShared/external.uris'
import { IDealerInfo } from '@appShared/interfaces/[Interface-based]/dealer-info.interface'
import { NotificationLevel_ } from '@appShared/services/lookup/[CodeGen]'

/** One tile on the landing page. Exactly one of gen2Route / routerLink / externalUri is set. */
interface DashboardTile {
   title: string
   description: string
   icon: string            // Font Awesome solid class, e.g. 'fa-file-signature'
   gen2Route?: RouteModel
   routerLink?: string
   externalUri?: string
}

interface DashboardTileGroup {
   title: string
   tiles: DashboardTile[]
}

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.less'],
    imports: [RouterLink, SelectedDealerHeroComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent {
   private _internalRoutes = SITE_DIRECTORY_ROUTES.internal
   private _accountRoutes = this._internalRoutes.account
   /* route .uri values are built at start-up into the full routerLink path (same pattern as manage-account) */
   salesSchoolRegistrationUri = `/${this._internalRoutes.sales_school_registration.uri}`
   remitOnlineUri = `/${this._accountRoutes.remittance.uri}`
   merchandiseReturnsUri = `/${this._internalRoutes.merchandise_returns.uri}`
   manageAccountUri = `/${this._accountRoutes.manage.uri}`

   private _appSignals = inject(AppSignals)
   private _commonService = inject(CommonService)
   currentProfile = computed(() => this._appSignals.currentProfile())
   dealerTrainingUri = computed(() => this._appSignals.dealerTrainingUri())
   mediaResourceUri = computed(() => this._appSignals.mediaResourceCenterUri())
   salesPortalUri = computed(() => this._appSignals.salesPortalUri())

   private _dealerService = inject(DealerService)
   selectedDealer = computed(() => this._dealerService.selectedDealer())
   canAccessSSRegistration = this._dealerService.canAccessSSRegistration

   gen2Routes = GEN2_ROUTES
   external_uris = EXTERNAL_URIS
   teamHubUri = computed(() => {
      const dealerInfo = this.currentProfile()?.dealerInfo
      const teamHubRoute = this.external_uris.team_hub


      if (dealerInfo) {
         return !dealerInfo.isHighlander
            ? dealerInfo.isUSDealer
               ? teamHubRoute.uri
               : teamHubRoute.alt_uri
            : null
      }

      return teamHubRoute.uri
   })

   /* ------------------------------------------------------------ welcome hero */

   /** "Good morning" / "Good afternoon" / "Good evening" from the browser clock. */
   greeting = computed(() => {
      const hour = new Date().getHours()
      return hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
   })
   /* ---------------------------------------------------------------- tiles */

   /** The seven landing-page destinations, grouped. Team Hub only shows when the dealer has one. */
   tileGroups = computed<DashboardTileGroup[]>(() => {
      const teamHubUri = this.teamHubUri()

      return [
         {
            title: 'Your business',
            tiles: [
               { title: 'Record of Sales', description: 'Enter and review your customer sales', icon: 'fa-file-signature', gen2Route: this.gen2Routes.record_of_sale },
               { title: 'E-Bar', description: 'Submit your weekly business activity report', icon: 'fa-chart-simple', gen2Route: this.gen2Routes.e_bar },
               { title: 'Activity Reports', description: 'See how your summer is tracking', icon: 'fa-chart-line', gen2Route: this.gen2Routes.activity_reports },
               { title: 'Manage Account', description: 'Orders, statements, remittances and reports', icon: 'fa-user-gear', routerLink: this.manageAccountUri }
            ]
         },
         {
            title: 'Learn & connect',
            tiles: [
               { title: 'Training Courses', description: 'Sharpen your skills between the doors', icon: 'fa-graduation-cap', externalUri: this.dealerTrainingUri() },
               { title: 'Media Resource Center', description: 'Audio, video and downloads for the field', icon: 'fa-headphones', externalUri: this.mediaResourceUri() },
               ...(teamHubUri
                  ? [{ title: 'Team Hub', description: 'Connect with your team and leaders', icon: 'fa-people-group', externalUri: teamHubUri }]
                  : [])
            ]
         }
      ]
   })

   /** Smaller secondary shortcuts under the tiles. */
   quickLinks = computed<DashboardTile[]>(() => {
      const profile = this.currentProfile()
      const links: DashboardTile[] = [
         { title: 'Remit Online', description: '', icon: 'fa-money-bill-transfer', routerLink: this.remitOnlineUri },
         { title: this.gen2Routes.dealer_order_processing.title, description: '', icon: 'fa-cart-shopping', gen2Route: this.gen2Routes.dealer_order_processing },
         { title: this.gen2Routes.check_in.title, description: '', icon: 'fa-clipboard-check', gen2Route: this.gen2Routes.check_in }
      ]

      if (this.canAccessSSRegistration()) {
         links.push({ title: 'Sales School Registration', description: '', icon: 'fa-school', routerLink: this.salesSchoolRegistrationUri })
      }

      if (profile?.canViewPendingMerchandiseReturns) {
         links.push({ title: 'Merchandise Returns', description: '', icon: 'fa-rotate-left', routerLink: this.merchandiseReturnsUri })
      }

      return links
   })

   /* ---------------------------------------------------------- notifications */

   /** Up to three un-cleared notifications, newest first, with safe HTML and a level class. */
   notifications = computed(() =>
      this._dealerService.notifications()
         .filter(notification => !notification.isCleared)
         .slice(0, 3)
         .map(notification => ({
            ...notification,
            levelClass: this._levelClass(notification.level),
            messageMarkup: this._commonService.transform(notification.message, SecurityContext.HTML)
         }))
   )

   notificationCount = computed(() => this._dealerService.notifications().filter(notification => !notification.isCleared).length)

   private _levelClass(level: NotificationLevel_): string {
      switch (level) {
         case NotificationLevel_.Important: return 'is-important'
         case NotificationLevel_.Urgent: return 'is-urgent'
         default: return 'is-info'
      }
   }

   /*
    public methods
   */

   goToPortal(route?: RouteModel): void {
      this._dealerService.navigateToGen2(route?.uri)
   }

   goToExternal(uri?: string): void {
      this._appSignals.navigateToExternal(uri)
   }

   /** Router links are handled by [routerLink]; this covers the other two kinds of tile. */
   open(tile: DashboardTile): void {
      if (tile.gen2Route) {
         this.goToPortal(tile.gen2Route)
      } else if (tile.externalUri) {
         this.goToExternal(tile.externalUri)
      }
   }

   seeAllNotifications(): void {
      this.goToPortal(this.gen2Routes.settings_notifications)
   }
}
